// Copyright 2023 Sorama B.V.

import { Unit, UnitType } from "units/Unit";
import { CompositeUnit } from "./CompositeUnit";
import { Power } from "./Power";
import { Time } from "./Time";
import { Volume } from "./Volume";

export class VolumeFlow extends UnitType {
    public static override typeName = "VolumeFlow";

    public static gallonsPerSecond: CompositeUnit = new CompositeUnit(Volume.gallons, Time.second, VolumeFlow);
    public static gallonsPerMinute: CompositeUnit = new CompositeUnit(Volume.gallons, Time.minute, VolumeFlow);
    public static gallonsPerHour: CompositeUnit = new CompositeUnit(Volume.gallons, Time.hour, VolumeFlow);

    public static cubicMetersPerSecond: CompositeUnit = new CompositeUnit(Volume.cubicMeters, Time.second, VolumeFlow);
    public static cubicMetersPerMinute: CompositeUnit = new CompositeUnit(Volume.cubicMeters, Time.minute, VolumeFlow);
    public static cubicMetersPerHour: CompositeUnit = new CompositeUnit(Volume.cubicMeters, Time.hour, VolumeFlow);

    public static milliLitersPerSecond: CompositeUnit = new CompositeUnit(Volume.milliLiters, Time.second, VolumeFlow);

    public static litersPerSecond: CompositeUnit = new CompositeUnit(Volume.liters, Time.second, VolumeFlow);
    public static litersPerMinute: CompositeUnit = new CompositeUnit(Volume.liters, Time.minute, VolumeFlow, {
        aliases: ["L/minute", "liter/minute"],
    });

    public static litersPerHour: CompositeUnit = new CompositeUnit(Volume.liters, Time.hour, VolumeFlow);

    public static hundredLiterPerMinute: Unit = new CompositeUnit(Volume.hectoLiter, Time.minute, VolumeFlow, {
        aliases: ["100 L/minute"],
        preferredUnit: "100 L/min",
    });
    public static thousandLiterPerMinute: Unit = new CompositeUnit(Volume.kiloLiter, Time.minute, VolumeFlow, {
        aliases: ["1000 L/min", "1000 Liters / Minute", "1000L/minute", "1000 L / minute", "kL / minute"],
        preferredUnit: "1000 L/min",
    });
    public static cubicFeetPerMinute: CompositeUnit = new CompositeUnit(Volume.cubicFeet, Time.minute, VolumeFlow, {
        aliases: ["cubic foot / minute", "CFM"],
        preferredUnit: "CFM",
    });
    public static hundredCubicFeetPerMinute: Unit = new Unit(
        "100CFM",
        VolumeFlow.cubicFeetPerMinute.conversion * 100,
        VolumeFlow
    );

    public static metricFlowUnits: Unit[] = [
        VolumeFlow.litersPerSecond,
        VolumeFlow.litersPerMinute,
        VolumeFlow.litersPerHour,
        VolumeFlow.cubicMetersPerSecond,
        VolumeFlow.cubicMetersPerMinute,
        VolumeFlow.cubicMetersPerHour,
        VolumeFlow.milliLitersPerSecond,
    ];
    public static imperialFlowUnits: Unit[] = [
        VolumeFlow.gallonsPerSecond,
        VolumeFlow.gallonsPerMinute,
        VolumeFlow.gallonsPerHour,
        VolumeFlow.cubicFeetPerMinute,
        VolumeFlow.hundredCubicFeetPerMinute,
    ];
}

export class FlowEffort extends UnitType {
    public static typeName = "Flow effort";

    public static kwPerliterMinute: CompositeUnit = new CompositeUnit(
        Power.kiloWatts,
        VolumeFlow.litersPerMinute,
        FlowEffort,
        {
            aliases: ["kW/L/minute", "kW/liter/minute"],
        }
    );
    public static kwPer100LiterMinute: CompositeUnit = new CompositeUnit(
        Power.kiloWatts,
        VolumeFlow.hundredLiterPerMinute,
        FlowEffort,
        {
            aliases: ["kW/100L/minute"],
        }
    );
    public static kwPer1000LiterMinute: CompositeUnit = new CompositeUnit(
        Power.kiloWatts,
        VolumeFlow.thousandLiterPerMinute,
        FlowEffort,
        {
            aliases: ["kW/1000L/minute"],
        }
    );
    public static kwPer100Cfm: CompositeUnit = new CompositeUnit(
        Power.kiloWatts,
        VolumeFlow.hundredCubicFeetPerMinute,
        FlowEffort
    );
    public static kwPerCfm: CompositeUnit = new CompositeUnit(
        Power.kiloWatts,
        VolumeFlow.cubicFeetPerMinute,
        FlowEffort,
        {
            aliases: ["kW/ cubic foot / minute", "kW/CFM"],
        }
    );

    public static wPerCubicMeterPerSecond: CompositeUnit = new CompositeUnit(
        Power.watts,
        VolumeFlow.cubicMetersPerSecond,
        FlowEffort,
        {
            aliases: ["W/m³/s", "W/cubic meter / second"],
        }
    );

    public static metricFlowEffortUnits: Unit[] = [
        FlowEffort.kwPerliterMinute,
        FlowEffort.kwPer100LiterMinute,
        FlowEffort.kwPer1000LiterMinute,
        FlowEffort.wPerCubicMeterPerSecond,
    ];

    public static imperialFlowEffortUnits: Unit[] = [FlowEffort.kwPer100Cfm, FlowEffort.kwPerCfm];
}
